import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { OVERDRAWN_ESCROW_NOTE } from "@/features/projects/utils/escrow-balance";
import { StudioButton } from "@/features/studio/theme";
import { cn } from "@/lib/utils";
import { IMAGE_PLACEHOLDER, swapToPlaceholder } from "../utils/image-fallback";
import { ProgressRing } from "./dashboard-widgets";

// ============================================================================
// Project Hero Card
// ============================================================================

interface ProjectHeroProps {
	projectId: string;
	projectName: string;
	location: string;
	propertyType: string;
	area: string;
	nextMilestone: string | null;
	milestoneAmount: string | null;
	imageUrl: string;
	/** 1-based position of `nextMilestone`; null when none is pending. */
	stageNumber: number | null;
	totalStages: number;
	/** One entry per milestone in order: true when it is completed. */
	segments: boolean[];
	daysInProject: number;
	recentComment?: {
		content: string;
		authorName: string;
		timeAgo: string;
	} | null;
}

export function ProjectHero({
	projectId,
	projectName,
	location,
	area,
	propertyType,
	imageUrl,
	nextMilestone,
	milestoneAmount,
	stageNumber,
	totalStages,
	segments,
	daysInProject,
	recentComment,
}: ProjectHeroProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const safeProjectName = projectName || "Your Project";
	const safeLocation = location || "";
	const safeArea = area || "";

	return (
		<div
			className="dashboard-container"
			style={{ padding: "0 0 24px", position: "relative" }}
		>
			{/* Hero Image */}
			<div className="relative aspect-4/3 overflow-hidden rounded dashboard-hero-image md:aspect-16/7">
				<div
					className="absolute inset-0"
					style={{
						background:
							"linear-gradient(135deg, var(--color-dashboard-muted-dark) 0%, var(--color-dashboard-dark-elevated) 100%)",
					}}
				/>
				<img
					src={imageUrl || IMAGE_PLACEHOLDER}
					alt={`${safeProjectName} exterior`}
					className="absolute inset-0 h-full w-full object-cover opacity-80 filter-warm"
					onError={swapToPlaceholder}
				/>
				<div
					className="absolute inset-0"
					style={{
						background:
							"linear-gradient(color-mix(in srgb, var(--color-dashboard-scrim) 40%, transparent) 0%, transparent 35%, transparent 50%, color-mix(in srgb, var(--color-dashboard-scrim) 65%, transparent) 100%), radial-gradient(at 75% 70%, transparent 35%, color-mix(in srgb, var(--color-dashboard-scrim) 30%, transparent) 100%)",
					}}
				/>
				<div className="absolute top-5 left-5 text-dashboard-on-fill md:top-8 md:left-9">
					<p className="font-system text-[9px] font-semibold uppercase tracking-[0.22em] text-dashboard-homecard">
						RESUME
						{stageNumber !== null &&
							` · CHAPTER ${String(stageNumber).padStart(2, "0")}`}
					</p>
					<h3 className="mt-2 font-display text-[28px] md:text-[38px] font-medium tracking-[-0.015em] text-dashboard-on-fill">
						{safeProjectName}
					</h3>
					<p className="mt-1.5 font-body text-sm text-dashboard-homecard">
						{safeLocation}
						{safeLocation && safeArea && " · "}
						{safeArea} · {propertyType}
					</p>
				</div>
				{totalStages > 0 && (
					<div className="absolute bottom-9 right-5 text-right text-dashboard-on-fill md:right-9">
						<p className="font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-dashboard-homecard">
							NEXT MILESTONE
						</p>
						<p className="mt-1 font-display text-[22px] md:text-[28px] font-medium italic text-dashboard-on-fill">
							{nextMilestone
								? `${nextMilestone}.`
								: t("homeProgress.allComplete")}
						</p>
						{nextMilestone && milestoneAmount && (
							<p className="mt-1 font-body text-xs text-dashboard-homecard">
								<span className="tabular-nums font-semibold">
									{milestoneAmount}
								</span>{" "}
								{t("homeProgress.milestonePayment")}
							</p>
						)}
					</div>
				)}
			</div>

			{/* Project Stats Bar */}
			<div
				className={cn(
					"relative -mt-8.5 mx-4 grid grid-cols-1 gap-6 rounded-2xl border border-dashboard-border px-5 py-5 dashboard-stats-bar md:ml-10 md:mr-10 md:grid-cols-[30fr_45fr_25fr] md:gap-9 md:px-7",
				)}
			>
				<div className="relative pr-1 md:mr-[-8px] md:border-r md:border-r-[color-mix(in_srgb,var(--color-dashboard-warm-300)_55%,transparent)]">
					{totalStages > 0 ? (
						<>
							<div className="flex items-baseline gap-3">
								<span className="font-display text-[28px] font-medium text-dashboard-text tabular-nums leading-none">
									{String(stageNumber ?? totalStages).padStart(2, "0")}
								</span>
								<span className="font-display italic text-sm text-dashboard-muted">
									of {totalStages}
								</span>
							</div>
							<p className="mt-1.5 font-mono text-[8.5px] font-semibold uppercase tracking-[0.22em] text-dashboard-muted">
								STAGE · {daysInProject} DAYS IN
							</p>
							<div className="mt-4.5 flex gap-1.25" style={{ maxWidth: 200 }}>
								{segments.map((done, i) => (
									<div
										// biome-ignore lint/suspicious/noArrayIndexKey: segments are positional and carry no id
										key={i}
										className="h-1.25 flex-1 rounded-full"
										style={{
											background: done
												? "linear-gradient(var(--color-dashboard-accent-light) 0%, var(--color-dashboard-accent) 100%)"
												: "var(--color-dashboard-warm-100)",
											boxShadow: done
												? "inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-warm-100) 45%, transparent), 0 2px 8px -2px color-mix(in srgb, var(--color-dashboard-accent) 45%, transparent)"
												: "inset 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 10%, transparent)",
										}}
									/>
								))}
							</div>
						</>
					) : (
						<>
							<p className="font-display text-sm italic text-dashboard-muted">
								{t("homeProgress.noMilestones")}
							</p>
							<p className="mt-1.5 font-mono text-[8.5px] font-semibold uppercase tracking-[0.22em] text-dashboard-muted">
								{daysInProject} DAYS IN
							</p>
						</>
					)}
				</div>

				<div className="relative px-2">
					<p className="font-mono text-[8.5px] font-semibold uppercase tracking-[0.22em] text-dashboard-muted">
						{recentComment
							? `FIELD NOTE · ${recentComment.authorName.split(" ")[0].toUpperCase()} · ${recentComment.timeAgo}`
							: "FIELD NOTE"}
					</p>
					{recentComment ? (
						// data-sensitive: the same comment body `comment-thread.tsx`
						// masks, pulled onto the dashboard. See lib/posthog.ts.
						<p
							data-sensitive
							className="mt-2 font-display text-[21px] italic leading-relaxed text-dashboard-text"
						>
							&ldquo;{recentComment.content}&rdquo;
						</p>
					) : (
						<p className="mt-2 font-display text-sm italic text-dashboard-muted">
							{t("homeProgress.noNotes")}
						</p>
					)}
				</div>

				<div className="relative flex flex-col gap-2 md:flex-row md:items-end md:justify-end">
					<StudioButton
						className="w-full md:w-40"
						variant="secondary"
						size="md"
						style={{
							height: 44,
							paddingLeft: 20,
							paddingRight: 20,
							fontSize: 14,
							fontWeight: 600,
							justifyContent: "flex-start",
							borderRadius: "var(--radius-xl)",
						}}
						onClick={() => navigate("/projects")}
					>
						Open project
					</StudioButton>

					<button
						type="button"
						className="flex h-11 w-full md:w-50 cursor-pointer items-center gap-2 rounded-xl border-none px-5 font-body text-sm font-semibold text-dashboard-on-fill transition-all duration-200 dashboard-primary-button"
						onClick={() => navigate(`/projects/${projectId}?tab=boq`)}
					>
						Review materials
						<ArrowRight className="h-4 w-4" />
					</button>
				</div>
			</div>
		</div>
	);
}

// ============================================================================
// Home at a Glance Cards
// ============================================================================

interface HomeAtGlanceProps {
	project: {
		name: string;
		location: string;
		propertyType: string;
		area: string;
		startDate: string;
		startYear: string;
		handoverDate: string;
		handoverYear: string;
		stage: string | null;
		progress: number | null;
		daysIn: number;
		totalDays: number | null;
		imageUrl: string;
	};
	budget: {
		released: number;
		committed: number;
		awaiting: number;
		remaining: number;
		percentage: number;
	};
	projectId: string;
	onOpenProject?: () => void;
}

export function HomeAtGlance({
	project,
	budget,
	projectId,
}: HomeAtGlanceProps) {
	const navigate = useNavigate();
	// The sign goes in front of the rupee symbol: AWAITING RELEASE is negative
	// when more has been paid out than was funded (`computeBudget`).
	const formatCurrency = (amount: number) => {
		const sign = amount < 0 ? "-" : "";
		return `${sign}₹${Math.abs(amount).toLocaleString("en-IN")}`;
	};

	const safeProjectName = project.name || "Your Project";
	const safeLocation = project.location || "";
	const safeArea = project.area || "";

	return (
		<div className="dashboard-container pt-20">
			<div className="mb-8">
				<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
					YOUR HOME · AT A GLANCE
				</p>
				<h2 className="mt-2 font-display text-[42px] font-medium leading-[1.05] tracking-[-0.02em] text-dashboard-text">
					Where{" "}
					<span className="italic text-dashboard-accent-display">
						{safeProjectName}
					</span>{" "}
					stands tonight.
				</h2>
			</div>

			<div className="grid grid-cols-1 gap-4.5 items-stretch md:grid-cols-[1.55fr_1fr]">
				{/* Project Card */}
				<div className="relative overflow-hidden rounded-2xl border border-dashboard-border bg-dashboard-paper dashboard-surface-card-stronger flex flex-col">
					<div className="group relative flex-1 min-h-96 overflow-hidden md:min-h-80">
						<img
							src={project.imageUrl || IMAGE_PLACEHOLDER}
							alt={safeProjectName}
							className="absolute inset-0 h-full w-full object-cover transition-transform duration-1200 group-hover:scale-105 filter-card"
							onError={swapToPlaceholder}
						/>
						<div
							className="absolute inset-0"
							style={{
								background:
									"linear-gradient(color-mix(in srgb, var(--color-dashboard-scrim) 40%, transparent) 0%, transparent 35%, transparent 55%, color-mix(in srgb, var(--color-dashboard-scrim) 85%, transparent) 100%)",
							}}
						/>
						<div
							className="absolute inset-0 opacity-70 transition-opacity duration-700 group-hover:opacity-100"
							style={{
								background:
									"radial-gradient(at 85% 20%, color-mix(in srgb, var(--color-dashboard-accent-light) 18%, transparent) 0%, transparent 55%)",
							}}
						/>

						{/* Status Badge */}
						<div className="absolute top-5 left-6 right-6 flex items-center justify-between text-dashboard-on-fill">
							<span className="font-mono text-[9px] font-semibold uppercase tracking-[0.22em] text-dashboard-homecard">
								I · ACTIVE PROJECT
							</span>
							<span className="dashboard-glass-pill inline-flex items-center gap-2 rounded-full px-3 py-1 text-[9px] text-dashboard-on-photo-warm font-semibold uppercase tracking-[0.18em]">
								<span
									className="h-1.25 w-1.25 rounded-full"
									style={{
										background: "var(--color-dashboard-accent-hover)",
										boxShadow:
											"0 0 6px color-mix(in srgb, var(--color-dashboard-accent-hover) 80%, transparent)",
									}}
								/>
								DAY {project.daysIn}
								{project.totalDays !== null && `/${project.totalDays}`}
							</span>
						</div>

						{/* Content */}
						<div className="absolute left-6 right-6 bottom-5 text-dashboard-on-fill">
							<p className="font-display text-[32px] md:text-[44px] font-medium leading-[0.95] text-dashboard-on-fill">
								{safeProjectName}{" "}
							</p>
							<p className="mt-2 font-body text-sm italic text-dashboard-homecard">
								{safeLocation}
								{safeLocation && safeArea && " · "}
								{safeArea} · {project.propertyType}
							</p>
							<div className="mt-5 flex flex-wrap items-end gap-5.5 md:flex-nowrap">
								<div>
									<p className="font-mono text-[8.5px] font-bold uppercase tracking-[0.22em] text-dashboard-homecard">
										STARTED
									</p>
									<p className="font-display text-sm text-dashboard-on-fill tabular-nums mt-1">
										{project.startDate}
									</p>
									<p className="font-body text-xs italic text-dashboard-homecard mt-0.5">
										{project.startYear}
									</p>
								</div>
								<div>
									<p className="font-mono text-[8.5px] font-bold uppercase tracking-[0.22em] text-dashboard-homecard">
										HANDOVER
									</p>
									<p className="font-display text-sm text-dashboard-on-fill tabular-nums mt-1">
										{project.handoverDate}
									</p>
									<p className="font-body text-xs italic text-dashboard-homecard mt-0.5">
										{project.handoverYear}
									</p>
								</div>
								<div>
									<p className="font-mono text-[8.5px] font-bold uppercase tracking-[0.22em] text-dashboard-homecard">
										STAGE
									</p>
									<p className="font-display text-sm italic text-dashboard-on-fill tabular-nums mt-1">
										{project.stage ?? "—"}
									</p>
									{project.progress !== null && (
										<p className="font-body text-xs italic text-dashboard-homecard mt-0.5">
											{project.progress}% complete
										</p>
									)}
								</div>
								<div className="flex-1" />
								<button
									type="button"
									className="flex h-10.5 items-center gap-2 rounded-xl border-none px-5 font-body text-sm font-semibold text-dashboard-on-fill transition-all duration-300 group-hover:translate-y-0.5 group-hover:scale-[1.02] dashboard-primary-button"
									onClick={() =>
										navigate(`/projects/${projectId}?tab=overview`)
									}
								>
									Open project
									<ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5" />
								</button>
							</div>
						</div>
					</div>
				</div>

				{/* Budget Card */}
				<div className="relative overflow-hidden rounded-2xl border border-dashboard-border px-6 py-6 dashboard-budget-card">
					<div
						className="absolute -top-15 -right-15 h-55 w-55 rounded-full"
						style={{
							background:
								"radial-gradient(circle, color-mix(in srgb, var(--color-dashboard-accent-light) 10%, transparent) 0%, transparent 65%)",
						}}
					/>
					<div className="flex relative justify-between items-baseline">
						<div className="font-mono text-[9px] font-semibold text-dashboard-muted uppercase tracking-[0.22em]">
							II · ESCROW · BUDGET
						</div>
					</div>
					<div className="relative flex mt-4.5 items-center gap-5.5">
						<ProgressRing percentage={budget.percentage} />
						<div className="flex-1">
							<p className="font-mono text-[8.5px] font-semibold uppercase tracking-[0.2em] text-dashboard-muted">
								RELEASED
							</p>
							<p className="font-display text-[30px] font-medium text-dashboard-text tabular-nums leading-tight">
								{formatCurrency(budget.released)}
							</p>
							<p className="font-display text-sm italic text-dashboard-muted mt-1">
								of{" "}
								<span className="tabular-nums">
									{formatCurrency(budget.committed)}
								</span>{" "}
								committed
							</p>
						</div>
					</div>
					<div
						className="relative mt-5 grid grid-cols-[1fr_1px_1fr] items-stretch gap-[14px] pt-4.5"
						style={{
							borderTop:
								"1px solid color-mix(in srgb, var(--color-border-default) 55%, transparent)",
						}}
					>
						<div>
							<p className="font-mono text-[8.5px] font-semibold uppercase tracking-[0.2em] text-dashboard-muted">
								AWAITING RELEASE
							</p>
							<p className="font-display text-lg italic font-medium text-dashboard-accent-dark tabular-nums mt-1">
								{formatCurrency(budget.awaiting)}
							</p>
							{budget.awaiting < 0 && (
								<p className="font-body text-[11px] text-dashboard-muted mt-1">
									{OVERDRAWN_ESCROW_NOTE}
								</p>
							)}
						</div>
						<div
							className="w-px"
							style={{
								background:
									"linear-gradient(transparent, color-mix(in srgb, var(--color-border-default) 70%, transparent), transparent)",
							}}
						/>
						<div>
							<p className="font-mono text-[8.5px] font-semibold uppercase tracking-[0.2em] text-dashboard-muted">
								REMAINING
							</p>
							<p className="font-display text-lg italic font-medium text-dashboard-text tabular-nums mt-1">
								{formatCurrency(budget.remaining)}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
